/**
 * Pure backsplash geometry, shared by the web-app "Add Backsplash" path and the
 * kitchen styling tool. No Three.js / DOM — just math over plain room data.
 *
 * Extracted from web-app/src/utils/fixtureTextureRegionUtils.js so both the
 * client (per-wall "Add Backsplash" button) and the server-side style_kitchen
 * tool compute regions identically. Regions are expressed as percentages of the
 * wall rectangle: { x, y, width, height } in [0,100], the shape the wall
 * texture-region renderer expects (`surfaceType: 'backsplash'`).
 */

const { wallFaceSpanY } = require('../../geometry/wallFace');

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * The 4 XZ bounding-box corners of an object, accounting for Y rotation.
 */
function getObjectXZCorners(obj) {
  const cx = obj.center?.x ?? 0;
  const cz = obj.center?.z ?? 0;
  const dimX = obj.dimensions?.x ?? 0;
  const dimZ = obj.dimensions?.z ?? 0;
  const rotY = obj.rotation?.y ?? 0;
  const cosR = Math.cos(rotY);
  const sinR = Math.sin(rotY);
  const hx = dimX / 2;
  const hz = dimZ / 2;
  const local = [
    { x: -hx, z: -hz },
    { x: hx, z: -hz },
    { x: hx, z: hz },
    { x: -hx, z: hz },
  ];
  return local.map(({ x, z }) => ({
    x: cx + x * cosR - z * sinR,
    z: cz + x * sinR + z * cosR,
  }));
}

/**
 * Wall segment endpoints in XZ. Tries bottomCorners, then alignment, then
 * center+dimensions+rotation. Returns { start:{x,z}, end:{x,z} } or null.
 */
function getWallEndpointsXZ(wall) {
  if (wall.bottomCorners && wall.bottomCorners.length >= 2) {
    return {
      start: { x: wall.bottomCorners[0].x, z: wall.bottomCorners[0].z },
      end: { x: wall.bottomCorners[1].x, z: wall.bottomCorners[1].z },
    };
  }
  if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
    return {
      start: { x: wall.alignment.bottomLeft.x, z: wall.alignment.bottomLeft.z },
      end: { x: wall.alignment.bottomRight.x, z: wall.alignment.bottomRight.z },
    };
  }
  if (wall.center && wall.dimensions) {
    const rotY = wall.rotation?.y || 0;
    const cosR = Math.cos(rotY);
    const sinR = Math.sin(rotY);
    const halfLen = (wall.dimensions.x ?? 0) / 2;
    return {
      start: {
        x: wall.center.x - cosR * halfLen,
        z: wall.center.z - sinR * halfLen,
      },
      end: {
        x: wall.center.x + cosR * halfLen,
        z: wall.center.z + sinR * halfLen,
      },
    };
  }
  return null;
}

/**
 * Project a set of objects' XZ corners onto the wall direction; return the
 * along-wall min/max as percentages [0,100], clamped, or null when degenerate.
 */
function projectExtentOntoWall(objects, wall) {
  const endpoints = getWallEndpointsXZ(wall);
  if (!endpoints) return null;
  const wallVec = {
    x: endpoints.end.x - endpoints.start.x,
    z: endpoints.end.z - endpoints.start.z,
  };
  const wallLenSq = wallVec.x * wallVec.x + wallVec.z * wallVec.z;
  if (wallLenSq === 0) return null;

  const project = (pt) =>
    (((pt.x - endpoints.start.x) * wallVec.x +
      (pt.z - endpoints.start.z) * wallVec.z) /
      wallLenSq) *
    100;

  let minP = 100;
  let maxP = 0;
  for (const obj of objects) {
    if (!obj.center || !obj.dimensions) continue;
    for (const corner of getObjectXZCorners(obj)) {
      const p = project(corner);
      minP = Math.min(minP, p);
      maxP = Math.max(maxP, p);
    }
  }
  if (minP >= maxP) return null;
  return { minP: Math.max(0, minP), maxP: Math.min(100, maxP) };
}

/**
 * Does this object's footprint stand AGAINST this wall?
 *
 * A counter surface belongs to the wall behind it, and the object's own
 * `parentWallIds` / `cabinetWallRefs` are only PART of that answer. At an
 * inside corner a run's last piece butts its END into the perpendicular wall:
 * the corner countertop is genuinely on the wall it names, and equally
 * genuinely against the one it does not. Neither the corner unit nor an
 * ordinary base cabinet whose run simply ends at a wall says so — and the wall
 * above that return is real wall, so it wants a backsplash like any other.
 *
 * The test is the object's nearest footprint corner against the wall's own
 * plane, plus an along-wall overlap so a cabinet in line with the wall but
 * past its end doesn't count. `toleranceM` covers the gap cabinetry is set
 * with; the wall's own half-thickness is added because a wall's endpoints
 * describe its centre line when it has thickness at all.
 */
function objectAbutsWall(obj, wall, toleranceM = 0.03) {
  if (!obj?.center || !obj?.dimensions || !wall) return false;
  const endpoints = getWallEndpointsXZ(wall);
  if (!endpoints) return false;
  const vx = endpoints.end.x - endpoints.start.x;
  const vz = endpoints.end.z - endpoints.start.z;
  const len = Math.hypot(vx, vz);
  if (!len) return false;
  const nx = -vz / len;
  const nz = vx / len;
  const nearest = Math.min(
    ...getObjectXZCorners(obj).map((c) =>
      Math.abs((c.x - endpoints.start.x) * nx + (c.z - endpoints.start.z) * nz)
    )
  );
  const halfThickness = Math.abs(wall.dimensions?.z ?? 0) / 2;
  if (nearest > halfThickness + toleranceM) return false;
  const extent = projectExtentOntoWall([obj], wall);
  return Boolean(extent && extent.maxP > 0 && extent.minP < 100);
}

/**
 * Build a backsplash region (percent rect) from an along-wall extent and an
 * explicit world-Y band [bottomY, topY] on the given wall.
 * @returns {{x:number,y:number,width:number,height:number}|null}
 */
function bandRegion({ minP, maxP, bottomY, topY, wall }) {
  // Percentages of the DRAWN face (`wallFace`), which is what the 3D and the
  // elevations tile a region over — not the stored box, which on a face
  // that follows a raised ceiling put the band 0.94" above its counter.
  const face = wallFaceSpanY(wall);
  if (!face || topY <= bottomY) return null;
  const startPercent = ((bottomY - face.bottom) / face.height) * 100;
  const heightPercent = ((topY - bottomY) / face.height) * 100;
  const y = clamp(startPercent, 0, 100);
  return {
    x: clamp(minP, 0, 100),
    y,
    // y + height must stay inside the wall rect — a band whose topY overruns
    // the wall's own top (e.g. a sloped-ceiling room) is clipped to the wall.
    width: Math.min(100, maxP - minP),
    height: clamp(heightPercent, 0, 100 - y),
  };
}

module.exports = {
  getObjectXZCorners,
  getWallEndpointsXZ,
  projectExtentOntoWall,
  objectAbutsWall,
  bandRegion,
};
